import React, { useState, useEffect } from "react";

const PaymentAllocation = ({ splitPayments, setSplitPayments, total }) => {
  const [roundOff, setRoundOff] = useState(false);
  const [adjustedTotal, setAdjustedTotal] = useState(total);

  const paymentMethods = ["Cash", "UPI", "Card", "Net Banking"];

  useEffect(() => {
    const newTotal = roundOff ? Math.ceil(total) : total;
    setAdjustedTotal(newTotal);

    if (splitPayments.length > 0) {
      // Ensure each payment amount defaults to 0 if invalid
      const totalAllocated = splitPayments.reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
      );
      const difference = newTotal - totalAllocated;

      if (difference !== 0) {
        const newPayments = [...splitPayments];
        const lastPayment = newPayments[newPayments.length - 1];
        lastPayment.amount = (Number(lastPayment.amount) || 0) + difference;
        setSplitPayments(newPayments);
      }
    }
    // We only need to run this effect when roundOff or total changes
  }, [roundOff, total]);

  const handlePaymentChange = (index, field, value) => {
    let newPayments = [...splitPayments];

    if (field === "amount") {
      const amount = parseFloat(value) || 0;
      const allocated = splitPayments.reduce(
        (sum, p, i) => sum + (i !== index ? Number(p.amount) || 0 : 0),
        0
      );
      const remaining = adjustedTotal - allocated;

      // If entered amount exceeds the remaining, use remaining
      newPayments[index][field] = amount > remaining ? remaining : amount;
    } else {
      newPayments[index][field] = value;
    }

    setSplitPayments(newPayments);
  };

  const totalAllocated = splitPayments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0
  );
  const remainingAmount = adjustedTotal - totalAllocated;
  const isAddDisabled = remainingAmount <= 0;

  const addPaymentMethod = () => {
    if (remainingAmount > 0) {
      setSplitPayments((prev) => [
        ...prev,
        { type: "", amount: remainingAmount }, // Prefill with remaining amount
      ]);
    }
  };

  const removePaymentMethod = (index) => {
    setSplitPayments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 mt-6">
      <h3 className="text-2xl font-bold text-gray-800 mb-4">
        Payment Allocation
      </h3>

      {/* Round Off Checkbox */}
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          id="roundOff"
          checked={roundOff}
          onChange={() => setRoundOff(!roundOff)}
          className="mr-2 w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
        />
        <label htmlFor="roundOff" className="text-gray-700 cursor-pointer">
          Round Off (₹{Math.ceil(total)})
        </label>
      </div>

      {splitPayments.map((payment, index) => (
        <div key={index} className="flex items-center space-x-3 mb-4">
          {/* Payment Method Select */}
          <select
            value={payment.type}
            onChange={(e) => handlePaymentChange(index, "type", e.target.value)}
            className="w-40 px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-blue-300 focus:border-blue-500"
          >
            <option value="" disabled>
              Select Method
            </option>
            {paymentMethods.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          {/* Amount Input */}
          <div className="relative">
            <input
              type="number"
              value={payment.amount}
              onChange={(e) =>
                handlePaymentChange(index, "amount", e.target.value)
              }
              className="w-28 px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-blue-300 focus:border-blue-500"
              min="0"
              max={adjustedTotal}
            />
            <span className="absolute right-3 top-3 text-gray-600 text-sm">
              ₹
            </span>
          </div>

          {/* Remove Button */}
          {splitPayments.length > 1 && (
            <button
              onClick={() => removePaymentMethod(index)}
              className="text-red-600 hover:text-red-800 px-2 py-1 rounded"
            >
              ✕
            </button>
          )}
        </div>
      ))}

      {/* Display Remaining Amount */}
      {remainingAmount > 0 && (
        <p className="text-sm text-red-500 mb-3">
          Remaining Amount to Allocate: ₹{remainingAmount.toFixed(2)}
        </p>
      )}

      {/* Add Payment Button */}
      <button
        onClick={addPaymentMethod}
        className={`mt-4 px-4 py-2 rounded shadow-md transition duration-200 ${
          isAddDisabled
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700 text-white"
        }`}
        disabled={isAddDisabled}
      >
        Add Payment Method
      </button>
    </div>
  );
};

export default PaymentAllocation;
